import React, { useMemo, useState } from "react";
import { Accordion, Card, useAccordionToggle } from "react-bootstrap";
import { useReportContext } from "../ReportDataContext";
import CampFilters, { useCampFilters } from "../CampFilters";
import {
  buildCampSummary,
  downloadCampSummarySheet,
} from "../CampReport/campSummaryExcel";

// Accordion header for one week: shows the week range and that week's
// Total / AM / PM, with a +/- icon reflecting the open state. The first week
// (index 0) starts open to mirror the Accordion's defaultActiveKey.
function WeekToggle({ eventKey, week, initialOpen }) {
  const [open, setOpen] = useState(initialOpen);
  const headerOnClick = useAccordionToggle(eventKey, () => setOpen(!open));

  return (
    <Card.Header
      onClick={headerOnClick}
      className="d-flex align-items-center"
      style={{ cursor: "pointer" }}
    >
      <span className="font-weight-bold">
        <i
          className={`fa mr-2 ${
            open ? "fa-minus text-muted" : "fa-plus text-success"
          }`}
        />
        {week.week}
      </span>
      <span className="text-muted ml-5">
        Total: <b>{week.total}</b>
        <span className="ml-4">
          AM: <b>{week.am}</b>
        </span>
        <span className="ml-4">
          PM: <b>{week.pm}</b>
        </span>
      </span>
    </Card.Header>
  );
}

// Streams x camp types table for one scope (a week, or the grand total). Zero
// counts stay blank, like the Excel pivot. `footer` is the bold subtotal row.
function SummaryTable({ campTypes, rows, footer, footerLabel, firstColLabel = "Stream" }) {
  return (
    <div className="table-responsive">
      <table className="table table-sm table-bordered mb-0">
        <thead>
          <tr className="text-center">
            <th className="text-left">{firstColLabel}</th>
            {campTypes.map((ct) => (
              <th key={ct}>{ct}</th>
            ))}
            <th>Grand Total</th>
            <th>AM</th>
            <th>PM</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code || row.abbr} className="text-center">
              <td className="text-left">{row.abbr}</td>
              {campTypes.map((ct) => (
                <td key={ct}>{row.byType[ct] || ""}</td>
              ))}
              <td>{row.total}</td>
              <td>{row.am}</td>
              <td>{row.pm}</td>
            </tr>
          ))}
          <tr className="text-center font-weight-bold bg-light">
            <td className="text-left">{footerLabel}</td>
            {campTypes.map((ct) => (
              <td key={ct}>{footer.byType[ct] || ""}</td>
            ))}
            <td>{footer.total}</td>
            <td>{footer.am}</td>
            <td>{footer.pm}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function CampSummaryReportView({ reports }) {
  const branch = useReportContext().AUTH.branch;

  // Same filters as the Registered Student List report; the camp endpoint
  // returns the program name as `program_type`.
  const { filteredReports, filters } = useCampFilters(reports, branch, {
    programField: "program_type",
  });

  const { campTypes, weeks, grand } = useMemo(
    () => buildCampSummary(filteredReports),
    [filteredReports]
  );

  return (
    <div>
      <CampFilters filters={filters} />

      <div className="row mb-3">
        <div className="col-12 text-right">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!filteredReports.length}
            onClick={() =>
              downloadCampSummarySheet(filteredReports, branch.branch_name)
            }
          >
            Download Summary
          </button>
        </div>
      </div>

      {weeks.length > 0 ? (
        <>
          <Accordion defaultActiveKey="0">
            {weeks.map((week, i) => (
              <Card key={week.week}>
                <WeekToggle
                  eventKey={String(i)}
                  week={week}
                  initialOpen={i === 0}
                />
                <Accordion.Collapse eventKey={String(i)}>
                  <Card.Body>
                    <SummaryTable
                      campTypes={campTypes}
                      rows={week.streams}
                      footer={week}
                      footerLabel="Week Total"
                    />
                  </Card.Body>
                </Accordion.Collapse>
              </Card>
            ))}
          </Accordion>

          <Card className="mt-3">
            <Card.Body>
              <SummaryTable
                campTypes={campTypes}
                rows={[]}
                footer={grand}
                footerLabel="Grand Total"
                firstColLabel=""
              />
            </Card.Body>
          </Card>
        </>
      ) : (
        <div className="text-muted mt-3">
          No results match the selected filters.
        </div>
      )}
    </div>
  );
}
